import React, { useState, useEffect } from 'react';
import { Wind, BarChart3, Calendar, Clock, TrendingUp } from 'lucide-react';
import { Button, Card, Select, Spinner, Alert } from '../design-system';
import groundingService from '../../services/groundingService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Grounding Analytics Component for Patients
 * Displays grounding usage statistics and charts
 */
const GroundingAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await groundingService.getAnalytics(user, null, timeRange);
      setAnalytics(response.analytics);
    } catch (error) {
      console.error('Error loading grounding analytics:', error);
      toast.error('Failed to load grounding analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card padding="large">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <Spinner size="medium" />
            <span className="text-gray-600">Loading grounding analytics...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (!analytics) {
    return (
      <Card padding="large">
        <div className="text-center py-8">
          <Wind className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            No Grounding Data Available
          </h3>
          <p className="text-gray-600">
            Start using grounding techniques to see your progress here.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="large">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
            <Wind className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Current Assignment Usage</h2>
            <p className="text-sm text-gray-600">Track your current grounding technique</p>
          </div>
        </div>
        <Select 
          value={timeRange} 
          onChange={setTimeRange}
          options={[
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'quarter', label: 'This Quarter' }
          ]}
        />
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{analytics.totalSessions}</div>
          <div className="text-sm text-gray-600">Total Sessions</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {analytics.totalDuration ? 
              `${Math.floor(analytics.totalDuration / 60)}m ${analytics.totalDuration % 60}s` : 
              '0m 0s'
            }
          </div>
          <div className="text-sm text-gray-600">Total Duration</div>
        </div>
      </div>

      {/* Recent Activity */}
      {(() => {
        // Regroup sessions by local timezone dates
        const localUsageData = analytics.rawSessions ? 
          groundingService.regroupByLocalDates(analytics.usageData, analytics.rawSessions) : 
          analytics.usageData;
        
        return localUsageData.length > 0 ? (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {localUsageData.slice(-7).reverse().map((day, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium">
                      {groundingService.formatDateForDisplay(day.latestSession || day.createdAt || day.date)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>{day.sessions} session{day.sessions !== 1 ? 's' : ''}</span>
                    <span>{day.duration}s</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
        <div className="text-center py-8">
          <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">No activity recorded yet</p>
          <p className="text-sm text-gray-500 mt-1">Start using grounding techniques to see your progress here</p>
        </div>
        );
      })()}
    </Card>
  );
};

/**
 * Simplified Grounding Analytics Component for Therapists
 * Displays patient's grounding usage statistics without charts
 */
const PatientGroundingAnalytics = ({ patientId, patientName }) => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    loadAnalytics();
  }, [patientId, timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await groundingService.getAnalytics(user, patientId, timeRange);
      setAnalytics(response.analytics);
    } catch (error) {
      console.error('Error loading patient grounding analytics:', error);
      toast.error('Failed to load patient grounding analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card padding="large">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <Spinner size="medium" />
            <span className="text-gray-600">Loading analytics...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (!analytics) {
    return (
      <Card padding="large">
        <div className="text-center py-8">
          <Wind className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            No Grounding Data Available
          </h3>
          <p className="text-gray-600">
            {patientName} hasn't used grounding techniques yet.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="large">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
            <Wind className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Grounding Analytics</h3>
            <p className="text-sm text-gray-600">{patientName}'s practice</p>
          </div>
        </div>
        <Select 
          value={timeRange} 
          onChange={setTimeRange} 
          className="w-32"
          options={[
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'quarter', label: 'This Quarter' }
          ]}
        />
      </div>
      
      {/* Simple stats without complex grid */}
      <div className="space-y-4">
        <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
          <span className="text-gray-700">Total Sessions</span>
          <span className="text-2xl font-bold text-blue-600">{analytics.totalSessions}</span>
        </div>
        
        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
          <span className="text-gray-700">Average Duration</span>
          <span className="text-2xl font-bold text-green-600">{analytics.avgDuration}s</span>
        </div>
        
        <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
          <span className="text-gray-700">Current Streak</span>
          <span className="text-2xl font-bold text-purple-600">{analytics.streak}</span>
        </div>
        
        <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
          <span className="text-gray-700">Average per Day</span>
          <span className="text-2xl font-bold text-orange-600">
            {analytics.totalSessions > 0 ? Math.round(analytics.totalSessions / 30) : 0}
          </span>
        </div>
      </div>
    </Card>
  );
};

export { GroundingAnalytics, PatientGroundingAnalytics };
